import React, { useState } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { Box, Chip, Divider, IconButton, Stack, Typography } from '@mui/material'
import { Add, DragHandle, Undo, VisibilityOff, Visibility, DeleteForever } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { SetupForm } from './SetupForm'
import { Summary } from './Summary'
import { SubstituteList } from './SubstituteList'
import { TrainingWorkoutInterface } from '../../../models/interfaces/training-workout.interface'
import { TrainingWorkoutGroupInterface } from '../../../models/interfaces/training-workout-group.interface'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { showModalConfirm } from '../../../redux/reducers/modal-confirm/modal-confirm.actions'
import { removeTrainingWorkoutFromGroup, showModalAddSubstitute } from '../../../redux/reducers/training-workout/training-workout.actions'
import { showMessage } from '../../../redux/reducers/snackbar/snackbar.actions'
import { Thumbnail } from '../../Thumbnail'
import { selectedTrainingWorkoutType } from '../../../redux/reducers/training-workout/training-workout.selectors'
import { getRemoveURL } from '../functions'

type SortableItemProps = {
  data: TrainingWorkoutInterface | TrainingWorkoutGroupInterface
  index: number
  isGrouped?: boolean
  trainingId: string
}

export function SortableItem ({ data, index, isGrouped = false, trainingId }: SortableItemProps) {
  const dispatch = useAppDispatch()
  const trainingWorkoutType = useAppSelector(selectedTrainingWorkoutType)
  const trainingWorkout = data as TrainingWorkoutInterface
  const theme = useTheme()
  const { t } = useTranslation()
  const [isEditFormVisible, setIsEditFormVisible] = useState(false)
  const [isSubstitutesExpanded, setIsSubstitutesExpanded] = useState(false)

  const toggleEditFormVisibility = () => setIsEditFormVisible(!isEditFormVisible)
  const toggleSubstitutes = () => setIsSubstitutesExpanded(!isSubstitutesExpanded)

  const substitutes = trainingWorkout.substitutes ?? []

  const getBackgroundColor = (isDraggind: boolean) => {
    if (isDraggind) {
      return theme.palette.primary.main
    }
    if (isGrouped) {
      return theme.palette.grey[50]
    }
    return theme.palette.grey[800]
  }

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: 'none',
    background: getBackgroundColor(isDragging),
    marginTop: 8,
    borderRadius: 8,
    padding: '10px',
    ...draggableStyle
  })

  const handleConfirmRemove = () => {
    dispatch(showModalConfirm({
      reference: trainingWorkout.workout.name,
      url: getRemoveURL(trainingWorkout.id, trainingWorkoutType)
    }))
  }

  const handleRemoveFromGroup = async () => {
    try {
      await dispatch(removeTrainingWorkoutFromGroup({
        trainingWorkoutId: trainingWorkout.id,
        type: trainingWorkoutType
      }))
      dispatch(showMessage({ message: `${t('trainingWorkout.ungrouped_success')}` }))
    } catch (e: any) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  const handleAddSubstitute = () => {
    dispatch(showModalAddSubstitute({
      id: trainingId,
      parentTrainingWorkoutId: trainingWorkout.id,
      parentWorkoutId: trainingWorkout.workout.id,
      existingSubstituteWorkoutIds: substitutes.map(s => s.workout.id)
    }))
  }

  return (
    <Stack key={trainingWorkout.id}>
      <Draggable draggableId={trainingWorkout.id} key={trainingWorkout.id} index={index}>
        {(provided, snapshot) => (
          <Stack
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
            <Stack flex={1} flexDirection="row" alignItems="center">
              <Box mr={1}>
                <DragHandle/>
              </Box>
              <Box mr={1}>
                <Thumbnail data={trainingWorkout.workout} />
              </Box>
              <Stack direction="column" flex={1}>
                {<Stack borderRadius={0.5}>
                  <Stack direction="row" justifyContent="space-between" px={1} alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontSize={15} color={theme.palette.grey[400]} fontWeight={500}>
                        {t(`workouts.${trainingWorkout.workout.name}`, { defaultValue: trainingWorkout.workout.name })}
                      </Typography>
                      {substitutes.length > 0 && (
                        <Chip
                          label={`${substitutes.length} substituto(s)`}
                          size="small"
                          color="info"
                          onClick={toggleSubstitutes}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </Stack>
                    <Stack direction="row">
                      {!isGrouped &&
                        <IconButton
                          color="default"
                          onClick={handleAddSubstitute}
                          title={`${t('trainingWorkout.addSubstitute')}`}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      }
                      {!isGrouped &&
                        <IconButton color="default" onClick={toggleEditFormVisibility}>
                          { isEditFormVisible ? <Visibility></Visibility> : <VisibilityOff></VisibilityOff>}
                        </IconButton>
                      }
                      {isGrouped &&
                        <IconButton onClick={handleRemoveFromGroup} title='Desagrupar'>
                          <Undo sx={{ color: theme.palette.error.main }}/>
                        </IconButton>
                      }
                      {!isGrouped &&
                        <IconButton onClick={handleConfirmRemove} title='Excluir'>
                          <DeleteForever sx={{ color: theme.palette.error.main }} />
                        </IconButton>
                      }
                    </Stack>
                  </Stack>
                  {!isGrouped &&
                    <>
                      <Stack mx={0.5}>
                        <Divider />
                      </Stack>
                      {!isEditFormVisible
                        ? <Summary onPress={toggleEditFormVisibility} setup={data.setup} />
                        : <SetupForm
                          isGrouped={isGrouped}
                          trainingWorkoutId={data.id}
                          setup={data.setup}
                          workoutType={trainingWorkout.workout.type}
                          onClose={toggleEditFormVisibility} />
                      }
                    </>
                  }
                  {isSubstitutesExpanded && substitutes.length > 0 && (
                    <SubstituteList
                      parentId={trainingWorkout.id}
                      substitutes={substitutes}
                      isGrouped={isGrouped}
                    />
                  )}
                </Stack>}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Draggable>
    </Stack>
  )
}
